import { createFileRoute } from "@tanstack/react-router";
import { useState } from "react";
import {
  Check,
  Sparkles,
  Crown,
  Rocket,
  ArrowUpRight,
  ArrowDownRight,
  X,
  CreditCard,
  Calendar,
  Receipt,
  Download,
  Plus,
  AlertCircle,
} from "lucide-react";
import { toast } from "sonner";

import { useTranslation } from "react-i18next";
import { MerchantShell } from "@/components/merchant/merchant-shell";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { cn } from "@/lib/utils";
import { Riyal } from "@/components/ui/riyal";

export const Route = createFileRoute("/merchant/subscription")({
  head: () => ({
    meta: [
      { title: "Subscription & Billing — Spayzo Merchant" },
      { name: "description", content: "Manage your Spayzo merchant plan, payment method, and invoices." },
    ],
  }),
  component: SubscriptionPage,
});

const ACCENT = "#5958F3";
const NS = "pages.merchant.subscription";

type Plan = {
  id: string;
  name: string;
  monthly: number;
  yearly: number;
  blurb: string;
  features: string[];
  icon: React.ComponentType<{ className?: string }>;
  recommended?: boolean;
};

const PLATFORM_PLANS: Plan[] = [
  {
    id: "basic",
    name: "Basic",
    monthly: 19,
    yearly: 190,
    blurb: "For solo merchants getting started.",
    icon: Sparkles,
    features: ["Up to 3 spaces", "Unlimited bookings", "Basic analytics", "Email support"],
  },
  {
    id: "pro",
    name: "Pro",
    monthly: 49,
    yearly: 490,
    blurb: "For growing teams and busy venues.",
    icon: Rocket,
    recommended: true,
    features: [
      "Up to 15 spaces",
      "Smart offers & upsell",
      "Advanced analytics",
      "Priority support",
      "Custom branding",
    ],
  },
  {
    id: "premium",
    name: "Premium",
    monthly: 129,
    yearly: 1290,
    blurb: "For multi-location operators.",
    icon: Crown,
    features: ["Unlimited spaces", "Multi-location", "API & integrations", "Dedicated manager", "SLA 99.9%"],
  },
];

type Invoice = {
  id: string;
  dateKey: string;
  date: string;
  planKey: string;
  plan: string;
  amount: number;
  status: "Paid" | "Refunded" | "Failed";
};

const INITIAL_INVOICES: Invoice[] = [
  { id: "INV-2026-0042", dateKey: "may", date: "May 1, 2026", planKey: "proMonthly", plan: "Pro · Monthly", amount: 49, status: "Paid" },
  { id: "INV-2026-0031", dateKey: "apr", date: "Apr 1, 2026", planKey: "proMonthly", plan: "Pro · Monthly", amount: 49, status: "Paid" },
  { id: "INV-2026-0022", dateKey: "mar", date: "Mar 1, 2026", planKey: "proMonthly", plan: "Pro · Monthly", amount: 49, status: "Paid" },
  { id: "INV-2026-0011", dateKey: "feb", date: "Feb 1, 2026", planKey: "basicMonthly", plan: "Basic · Monthly", amount: 19, status: "Paid" },
  { id: "INV-2026-0002", dateKey: "jan", date: "Jan 1, 2026", planKey: "basicMonthly", plan: "Basic · Monthly", amount: 19, status: "Refunded" },
];

async function downloadInvoicePDF(inv: Invoice) {
  const { default: jsPDF } = await import("jspdf");
  const doc = new jsPDF();
  doc.setFontSize(20);
  doc.text("Spayzo Invoice", 20, 25);
  doc.setFontSize(10);
  doc.setTextColor(120);
  doc.text(inv.id, 20, 33);
  doc.setTextColor(0);

  doc.setFontSize(11);
  doc.text(`Date: ${inv.date}`, 20, 50);
  doc.text(`Plan: ${inv.plan}`, 20, 58);
  doc.text(`Status: ${inv.status}`, 20, 66);

  doc.setDrawColor(220);
  doc.line(20, 75, 190, 75);

  doc.setFontSize(12);
  doc.text("Description", 20, 85);
  doc.text("Amount", 170, 85, { align: "right" });
  doc.setFontSize(11);
  doc.text(inv.plan, 20, 95);
  doc.text(`SAR ${inv.amount.toFixed(2)}`, 170, 95, { align: "right" });

  doc.line(20, 105, 190, 105);
  doc.setFontSize(13);
  doc.text("Total", 20, 115);
  doc.text(`SAR ${inv.amount.toFixed(2)}`, 170, 115, { align: "right" });

  doc.setFontSize(9);
  doc.setTextColor(120);
  doc.text("Thank you for using Spayzo.", 20, 140);

  doc.save(`${inv.id}.pdf`);
}

function SubscriptionPage() {
  const { t } = useTranslation();
  const [current, setCurrent] = useState<string>("pro");
  const [cycle, setCycle] = useState<"monthly" | "yearly">("monthly");
  const [invoices] = useState<Invoice[]>(INITIAL_INVOICES);
  const [switchTo, setSwitchTo] = useState<Plan | null>(null);
  const [cancelOpen, setCancelOpen] = useState(false);
  const [payOpen, setPayOpen] = useState(false);
  const [card, setCard] = useState({ number: "•••• •••• •••• 4242", exp: "09/28", name: "Central Coffee Co." });
  const [cardDraft, setCardDraft] = useState(card);

  const planName = (p: Plan) => t(`${NS}.plans.${p.id}.name`, { defaultValue: p.name });
  const planBlurb = (p: Plan) => t(`${NS}.plans.${p.id}.blurb`, { defaultValue: p.blurb });
  const planFeatures = (p: Plan) => {
    const v = t(`${NS}.plans.${p.id}.features`, { returnObjects: true }) as unknown;
    return Array.isArray(v) ? (v as string[]) : p.features;
  };
  const renewDate = t(`${NS}.renewDate`);

  const currentPlan = PLATFORM_PLANS.find((p) => p.id === current)!;
  const nextPrice = cycle === "monthly" ? currentPlan.monthly : Math.round(currentPlan.yearly / 12);

  const applySwitch = () => {
    if (!switchTo) return;
    setCurrent(switchTo.id);
    toast.success(t(`${NS}.switchedToast`, { plan: planName(switchTo) }), {
      description: t(`${NS}.switchedDesc`, {
        amount: cycle === "monthly" ? switchTo.monthly : switchTo.yearly,
        suffix: cycle === "monthly" ? t(`${NS}.perMo`) : "/yr",
      }),
    });
    setSwitchTo(null);
  };

  const savePayment = () => {
    setCard(cardDraft);
    setPayOpen(false);
    toast.success(t(`${NS}.paymentUpdated`));
  };

  const exportAll = () => {
    const rows = [
      [t(`${NS}.table.invoice`), t(`${NS}.table.date`), t(`${NS}.table.plan`), t(`${NS}.table.amount`), t(`${NS}.table.status`)],
      ...invoices.map((i) => [i.id, i.date, i.plan, i.amount.toString(), i.status]),
    ];
    const csv = rows.map((r) => r.map((c) => `"${c}"`).join(",")).join("\n");
    const blob = new Blob([csv], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `invoices-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
    toast.success(t(`${NS}.exported`, { count: invoices.length }));
  };

  return (
    <MerchantShell
      title={t("merchantSubscription.title")}
      subtitle={t("merchantSubscription.subtitle")}
      actions={
        <Button
          variant="outline"
          className="h-10 rounded-xl"
          onClick={() => {
            setCardDraft(card);
            setPayOpen(true);
          }}
        >
          <CreditCard className="h-4 w-4" /> {t("merchantSubscription.updatePayment")}
        </Button>
      }
    >
      {/* Current plan hero */}
      <Card
        className="relative overflow-hidden rounded-2xl border-border/60 p-6 shadow-soft"
        style={{
          background: `linear-gradient(135deg, ${ACCENT}14 0%, transparent 60%)`,
        }}
      >
        <div
          className="pointer-events-none absolute -top-16 -end-16 h-48 w-48 rounded-full opacity-20 blur-3xl"
          style={{ backgroundColor: ACCENT }}
        />
        <div className="relative flex flex-wrap items-start justify-between gap-4">
          <div className="text-start">
            <Badge
              className="rounded-full text-white hover:opacity-90"
              style={{ backgroundColor: ACCENT }}
            >
              {t(`${NS}.activePlan`)}
            </Badge>
            <h2 className="mt-3 font-display text-3xl font-bold tracking-tight">{planName(currentPlan)}</h2>
            <p className="mt-1 text-sm text-muted-foreground">
              {t(`${NS}.renews`)} <span className="font-semibold text-foreground">{renewDate}</span> ·{" "}
              <Riyal /> {nextPrice}
              {t(`${NS}.perMonth`)}
            </p>
            <div className="mt-3 flex flex-wrap gap-4 text-xs text-muted-foreground">
              <span className="inline-flex items-center gap-1.5">
                <Calendar className="h-3.5 w-3.5" />{" "}
                {cycle === "monthly" ? t(`${NS}.billingMonthly`) : t(`${NS}.billingYearly`)}
              </span>
              <span className="inline-flex items-center gap-1.5">
                <CreditCard className="h-3.5 w-3.5" /> {t(`${NS}.visa`)} {card.number.slice(-4)}
              </span>
              <span className="inline-flex items-center gap-1.5">
                <Receipt className="h-3.5 w-3.5" /> {t(`${NS}.nextInvoice`)} <Riyal /> {nextPrice}.00
              </span>
            </div>
          </div>
          <div className="flex flex-wrap gap-2">
            <Button
              className="h-10 rounded-xl shadow-soft"
              style={{ backgroundColor: ACCENT, color: "white" }}
              onClick={() => {
                const idx = PLATFORM_PLANS.findIndex((p) => p.id === current);
                const next = PLATFORM_PLANS[idx + 1];
                if (next) setSwitchTo(next);
                else toast.info(t(`${NS}.topPlan`));
              }}
            >
              <ArrowUpRight className="h-4 w-4 rtl:-scale-x-100" /> {t(`${NS}.upgrade`)}
            </Button>
            <Button
              variant="outline"
              className="h-10 rounded-xl"
              onClick={() => {
                const idx = PLATFORM_PLANS.findIndex((p) => p.id === current);
                const prev = PLATFORM_PLANS[idx - 1];
                if (prev) setSwitchTo(prev);
                else toast.info(t(`${NS}.basePlan`));
              }}
            >
              <ArrowDownRight className="h-4 w-4 rtl:-scale-x-100" /> {t(`${NS}.downgrade`)}
            </Button>
            <Button
              variant="ghost"
              className="h-10 rounded-xl text-destructive hover:text-destructive"
              onClick={() => setCancelOpen(true)}
            >
              <X className="h-4 w-4" /> {t(`${NS}.cancel`)}
            </Button>
          </div>
        </div>

        <div className="relative mt-6 grid grid-cols-1 gap-2 sm:grid-cols-2">
          {planFeatures(currentPlan).map((f) => (
            <div key={f} className="flex items-center gap-2 text-sm">
              <span
                className="grid h-5 w-5 shrink-0 place-items-center rounded-full text-white"
                style={{ backgroundColor: ACCENT }}
              >
                <Check className="h-3 w-3" />
              </span>
              {f}
            </div>
          ))}
        </div>
      </Card>

      {/* Billing summary */}
      <div className="mt-6 grid grid-cols-1 gap-4 lg:grid-cols-3">
        <Card className="rounded-2xl border-border/60 p-6 shadow-soft lg:col-span-2">
          <p className="text-start text-xs font-semibold uppercase tracking-wider text-muted-foreground">
            {t(`${NS}.nextCharge`)}
          </p>
          <div className="mt-3 flex flex-wrap items-end justify-between gap-4">
            <div className="text-start">
              <p className="font-display text-3xl font-bold tracking-tight"><Riyal /> {nextPrice}.00</p>
              <p className="mt-1 flex items-center gap-1.5 text-sm text-muted-foreground">
                <Calendar className="h-3.5 w-3.5" /> {renewDate} · {planName(currentPlan)} {t(`${NS}.planSuffix`)}
              </p>
            </div>
            <div className="flex flex-wrap gap-2">
              <Button variant="outline" className="h-10 rounded-xl" onClick={exportAll}>
                <Download className="h-4 w-4" /> {t(`${NS}.exportReceipts`)}
              </Button>
              <Button
                className="h-10 rounded-xl shadow-soft"
                style={{ backgroundColor: ACCENT, color: "white" }}
                onClick={() => toast.success(t(`${NS}.paymentInitiated`))}
              >
                {t(`${NS}.payNow`)}
              </Button>
            </div>
          </div>
        </Card>

        <Card className="rounded-2xl border-border/60 p-6 shadow-soft">
          <p className="text-start text-xs font-semibold uppercase tracking-wider text-muted-foreground">
            {t(`${NS}.paymentMethod`)}
          </p>
          <div className="mt-3 flex items-center gap-3 rounded-xl border border-border/60 p-3">
            <div
              className="grid h-10 w-14 shrink-0 place-items-center rounded-lg text-[10px] font-bold text-white"
              style={{ backgroundColor: ACCENT }}
            >
              VISA
            </div>
            <div className="min-w-0 flex-1 text-start">
              <p className="text-sm font-semibold" dir="ltr">{card.number}</p>
              <p className="text-xs text-muted-foreground">{t(`${NS}.expires`, { date: card.exp })}</p>
            </div>
          </div>
          <Button
            variant="outline"
            className="mt-3 h-10 w-full rounded-xl"
            onClick={() => {
              setCardDraft(card);
              setPayOpen(true);
            }}
          >
            <CreditCard className="h-4 w-4" /> {t(`${NS}.updateCard`)}
          </Button>
        </Card>
      </div>

      {/* Switch plan */}
      <div className="mt-8 flex flex-wrap items-end justify-between gap-3">
        <div className="text-start">
          <h2 className="font-display text-xl font-bold tracking-tight">{t(`${NS}.switchPlan`)}</h2>
          <p className="mt-1 text-sm text-muted-foreground">{t(`${NS}.switchHint`)}</p>
        </div>
        <Tabs value={cycle} onValueChange={(v) => setCycle(v as typeof cycle)}>
          <TabsList className="rounded-xl">
            <TabsTrigger value="monthly" className="rounded-lg">
              {t(`${NS}.monthly`)}
            </TabsTrigger>
            <TabsTrigger value="yearly" className="rounded-lg">
              {t(`${NS}.yearlySave`)}
            </TabsTrigger>
          </TabsList>
        </Tabs>
      </div>

      <div className="mt-4 grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-3">
        {PLATFORM_PLANS.map((p) => {
          const isCurrent = p.id === current;
          const Icon = p.icon;
          const price = cycle === "monthly" ? p.monthly : Math.round(p.yearly / 12);
          return (
            <Card
              key={p.id}
              className={cn(
                "relative flex flex-col rounded-2xl p-6 text-start shadow-soft transition hover:shadow-md",
                p.recommended ? "ring-2" : "border-border/60",
                isCurrent && "bg-surface",
              )}
              style={p.recommended ? { boxShadow: `0 0 0 1px ${ACCENT}66` } : undefined}
            >
              {p.recommended && (
                <Badge
                  className="absolute -top-3 start-6 rounded-full text-white hover:opacity-90"
                  style={{ backgroundColor: ACCENT }}
                >
                  {t(`${NS}.recommended`)}
                </Badge>
              )}
              <div className="flex items-center gap-3">
                <div
                  className={cn(
                    "grid h-11 w-11 shrink-0 place-items-center rounded-xl",
                    !p.recommended && "bg-[#5958F3]/10",
                  )}
                  style={p.recommended ? { backgroundColor: ACCENT, color: "white" } : { color: ACCENT }}
                >
                  <Icon className="h-5 w-5" />
                </div>
                <div className="min-w-0">
                  <p className="truncate font-display text-lg font-bold">{planName(p)}</p>
                  <p className="truncate text-xs text-muted-foreground">{planBlurb(p)}</p>
                </div>
              </div>

              <div className="mt-5 flex items-baseline gap-1">
                <span className="font-display text-4xl font-bold tracking-tight"><Riyal /> {price}</span>
                <span className="text-sm text-muted-foreground">{t(`${NS}.perMo`)}</span>
              </div>
              {cycle === "yearly" && (
                <p className="mt-1 text-xs text-muted-foreground">{t(`${NS}.billedYearly`, { amount: p.yearly })}</p>
              )}

              <ul className="mt-5 space-y-2.5">
                {planFeatures(p).map((f) => (
                  <li key={f} className="flex items-start gap-2 text-sm">
                    <span
                      className="mt-0.5 grid h-4 w-4 shrink-0 place-items-center rounded-full text-white"
                      style={{ backgroundColor: ACCENT }}
                    >
                      <Check className="h-2.5 w-2.5" />
                    </span>
                    <span>{f}</span>
                  </li>
                ))}
              </ul>

              <div className="mt-6">
                <Button
                  disabled={isCurrent}
                  onClick={() => setSwitchTo(p)}
                  className="h-11 w-full rounded-xl"
                  style={
                    !isCurrent && p.recommended ? { backgroundColor: ACCENT, color: "white" } : undefined
                  }
                  variant={isCurrent ? "outline" : p.recommended ? "default" : "outline"}
                >
                  {isCurrent ? t(`${NS}.currentPlan`) : t(`${NS}.choosePlan`)}
                </Button>
              </div>
            </Card>
          );
        })}
      </div>

      {/* Billing history */}
      <Card className="mt-8 overflow-hidden rounded-2xl border-border/60 shadow-soft">
        <div className="flex flex-wrap items-center justify-between gap-3 p-5">
          <div className="flex items-center gap-2.5">
            <span
              className="grid h-9 w-9 shrink-0 place-items-center rounded-xl text-white"
              style={{ backgroundColor: ACCENT }}
            >
              <Receipt className="h-4 w-4" />
            </span>
            <div className="text-start">
              <p className="font-display text-base font-semibold">{t(`${NS}.billingHistory`)}</p>
              <p className="text-xs text-muted-foreground">
                {t(`${NS}.invoicesCount`, { count: invoices.length })}
              </p>
            </div>
          </div>
          <Button variant="outline" size="sm" className="h-9 rounded-xl text-xs" onClick={exportAll}>
            <Download className="h-3.5 w-3.5" /> {t(`${NS}.exportAll`)}
          </Button>
        </div>
        <div className="overflow-x-auto border-t border-border/60">
          <table className="w-full text-sm">
            <thead className="bg-surface text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
              <tr>
                <th className="p-3 text-start">{t(`${NS}.table.invoice`)}</th>
                <th className="p-3 text-start">{t(`${NS}.table.date`)}</th>
                <th className="p-3 text-start">{t(`${NS}.table.plan`)}</th>
                <th className="p-3 text-end">{t(`${NS}.table.amount`)}</th>
                <th className="p-3 text-start">{t(`${NS}.table.status`)}</th>
                <th className="w-10 p-3" />
              </tr>
            </thead>
            <tbody>
              {invoices.map((inv) => (
                <tr key={inv.id} className="border-t border-border/60 hover:bg-surface/60">
                  <td className="p-3 font-mono text-xs font-semibold" dir="ltr">{inv.id}</td>
                  <td className="p-3">{t(`${NS}.invoiceDates.${inv.dateKey}`, { defaultValue: inv.date })}</td>
                  <td className="p-3 text-muted-foreground">
                    {t(`${NS}.invoicePlans.${inv.planKey}`, { defaultValue: inv.plan })}
                  </td>
                  <td className="p-3 text-end font-semibold tabular-nums"><Riyal /> {inv.amount.toFixed(2)}</td>
                  <td className="p-3">
                    <span
                      className={cn(
                        "inline-flex items-center gap-1.5 rounded-full px-2 py-0.5 text-[11px] font-semibold ring-1 ring-inset",
                        inv.status === "Paid"
                          ? "bg-emerald-500/10 text-emerald-700 ring-emerald-500/20"
                          : inv.status === "Refunded"
                            ? "bg-violet-500/10 text-violet-700 ring-violet-500/20"
                            : "bg-rose-500/10 text-rose-700 ring-rose-500/20",
                      )}
                    >
                      {t(`${NS}.status.${inv.status}`, { defaultValue: inv.status })}
                    </span>
                  </td>
                  <td className="p-3">
                    <Button
                      size="icon"
                      variant="ghost"
                      className="h-8 w-8"
                      aria-label={t(`${NS}.downloadAria`)}
                      onClick={() => {
                        downloadInvoicePDF(inv);
                        toast.success(t(`${NS}.downloaded`, { id: inv.id }));
                      }}
                    >
                      <Download className="h-4 w-4" />
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>

      {/* Switch plan confirm */}
      <AlertDialog open={!!switchTo} onOpenChange={(o) => !o && setSwitchTo(null)}>
        <AlertDialogContent className="rounded-2xl text-start">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-start">
              {t(`${NS}.switchTitle`, { plan: switchTo ? planName(switchTo) : "" })}
            </AlertDialogTitle>
            <AlertDialogDescription className="text-start">
              {t(`${NS}.switchBodyPre`)}{" "}
              <span className="font-semibold text-foreground">
                <Riyal /> {cycle === "monthly" ? switchTo?.monthly : switchTo?.yearly}
                {cycle === "monthly" ? t(`${NS}.perMo`) : "/yr"}
              </span>{" "}
              {t(`${NS}.switchBodyPost`)}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="rounded-xl">{t(`${NS}.keepCurrent`)}</AlertDialogCancel>
            <AlertDialogAction
              onClick={applySwitch}
              className="rounded-xl"
              style={{ backgroundColor: ACCENT, color: "white" }}
            >
              {t(`${NS}.confirmSwitch`)}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Cancel confirm */}
      <AlertDialog open={cancelOpen} onOpenChange={setCancelOpen}>
        <AlertDialogContent className="rounded-2xl text-start">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-start">{t(`${NS}.cancelTitle`)}</AlertDialogTitle>
            <AlertDialogDescription className="text-start">
              {t(`${NS}.cancelBody`, { plan: planName(currentPlan), date: renewDate })}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <div className="flex items-center gap-2 rounded-xl bg-rose-500/5 p-3 text-xs text-rose-700">
            <AlertCircle className="h-4 w-4 shrink-0" />
            {t(`${NS}.cancelWarn`)}
          </div>
          <AlertDialogFooter>
            <AlertDialogCancel className="rounded-xl">{t(`${NS}.keepPlan`)}</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                setCancelOpen(false);
                toast.success(t(`${NS}.cancelledToast`), {
                  description: t(`${NS}.cancelledDesc`, { date: renewDate }),
                });
              }}
              className="rounded-xl bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {t(`${NS}.cancelConfirm`)}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Payment method dialog */}
      <Dialog open={payOpen} onOpenChange={setPayOpen}>
        <DialogContent className="rounded-2xl text-start sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="text-start">{t(`${NS}.payTitle`)}</DialogTitle>
            <DialogDescription className="text-start">{t(`${NS}.payDesc`)}</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label htmlFor="cc-name" className="text-xs">
                {t(`${NS}.cardName`)}
              </Label>
              <Input
                id="cc-name"
                className="mt-1.5 h-10 rounded-xl text-start"
                value={cardDraft.name}
                onChange={(e) => setCardDraft({ ...cardDraft, name: e.target.value })}
              />
            </div>
            <div>
              <Label htmlFor="cc-num" className="text-xs">
                {t(`${NS}.cardNumber`)}
              </Label>
              <Input
                id="cc-num"
                dir="ltr"
                className="mt-1.5 h-10 rounded-xl font-mono"
                placeholder="1234 5678 9012 3456"
                value={cardDraft.number}
                onChange={(e) => setCardDraft({ ...cardDraft, number: e.target.value })}
              />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label htmlFor="cc-exp" className="text-xs">
                  {t(`${NS}.expiry`)}
                </Label>
                <Input
                  id="cc-exp"
                  dir="ltr"
                  className="mt-1.5 h-10 rounded-xl"
                  placeholder="MM/YY"
                  value={cardDraft.exp}
                  onChange={(e) => setCardDraft({ ...cardDraft, exp: e.target.value })}
                />
              </div>
              <div>
                <Label htmlFor="cc-cvc" className="text-xs">
                  {t(`${NS}.cvc`)}
                </Label>
                <Input id="cc-cvc" dir="ltr" className="mt-1.5 h-10 rounded-xl" placeholder="123" />
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" className="rounded-xl" onClick={() => setPayOpen(false)}>
              {t(`${NS}.cancelBtn`)}
            </Button>
            <Button className="rounded-xl" style={{ backgroundColor: ACCENT, color: "white" }} onClick={savePayment}>
              <Plus className="h-4 w-4" /> {t(`${NS}.saveCard`)}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </MerchantShell>
  );
}
